'use client';

import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import type { LanguageIsoCode } from '@/graphql/graphql';
import type { Program } from '@/graphql/graphql';
import { Link } from '@/i18n/navigation';
import Autoplay from 'embla-carousel-autoplay';
import { useLocale } from 'next-intl';
import type React from 'react';
import { useRef } from 'react';
import { IconArrowUpRight } from '../../../components/ui/icons';
import Mediacard from './media-card';

type LocalizedHref = React.ComponentProps<typeof Link>['href'];

interface MediaCarouselProps {
    title: string | React.ReactNode;
    viewAllSrc: LocalizedHref;
    viewAllLabel: string;
    mediaList?: Program[];
    autoplay?: boolean;
}

export default function MediaCarousel({ title, viewAllSrc, viewAllLabel, mediaList, autoplay }: MediaCarouselProps) {
    const locale = useLocale().toLowerCase();
    const plugin = useRef(Autoplay({ delay: 2000, stopOnInteraction: false, stopOnMouseEnter: true }));

    return (
        <div>
            <div className="flex md:items-center md:justify-between mb-5 relative z-2">
                <h2 className="font-bold text-xl md:text-2xl mx-5 mb-0 md:mb-0 md:ml-18">
                    <Link href={viewAllSrc}>
                        {typeof title === 'string' ? (
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: title comes from backend already stylised
                            <span dangerouslySetInnerHTML={{ __html: title }} />
                        ) : (
                            title
                        )}
                    </Link>
                </h2>
                <Link className="flex ml-auto md:ml-5 mr-2 md:mr-18 underline items-end" href={viewAllSrc}>
                    <span className="hidden md:inline">{viewAllLabel}</span>{' '}
                    <IconArrowUpRight className="ml-0 md:ml-2 text-[#DD06AE]" />
                </Link>
            </div>
            <div className="relative z-1">
                <Carousel
                    plugins={autoplay ? [plugin.current] : []}
                    opts={{
                        align: 'start',
                        slidesToScroll: 1,
                    }}
                    className="mt-[-28px] lg:mt-[-35px]"
                >
                    <CarouselContent className="ml-2 md:ml-15 mr-5 py-7 lg:py-9">
                        {mediaList
                            ? mediaList.map((program: Program) => {
                                  return (
                                      <CarouselItem
                                          key={program.identifier.id}
                                          className={'basis-[calc(100%/3.3)] md:basis-[calc(100%/6.3)]'}
                                      >
                                          <Mediacard program={program} language={locale as LanguageIsoCode} />
                                      </CarouselItem>
                                  );
                              })
                            : null}
                    </CarouselContent>
                    <div className="hidden md:block absolute left-0 top-0 h-[100%] w-[95px] bg-[linear-gradient(90deg,rgba(14,14,14,0.3)_15.93%,rgba(49,49,49,0)_74.11%)]">
                        <CarouselPrevious
                            variant="ghost"
                            className="cursor-pointer absolute left-3 hover:bg-transparent disabled:opacity-0"
                        />
                    </div>
                    <div className="hidden md:block absolute right-0 top-0 h-[100%] w-[95px] bg-[linear-gradient(267deg,rgba(14,14,14,0.3)_15.93%,rgba(49,49,49,0)_74.11%)]">
                        <CarouselNext
                            variant="ghost"
                            className="cursor-pointer absolute right-3 hover:bg-transparent disabled:opacity-0"
                        />
                    </div>
                </Carousel>
            </div>
        </div>
    );
}
